import { type ColDef } from "ag-grid-enterprise";

import type UserTabPermissions from "types/UserTabPermissions/UserTabPermissions";

import {
    type BidPackageDraftDeadline,
    type IBidPackageDraft,
    type IBidPackageSuggestion,
    type ISubBidInsight,
} from "./AiBidPackageSuggestions.types";

// Matches the bid package Responses grid: drag to resize, but no sorting or header menus.
export const gridColumnDefaults: ColDef = {
    resizable: true,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressHeaderFilterButton: true,
};

// Mirrors the context endpoint's gates, so users it would reject are never offered the flow.
export const canSuggestBidPackages = (permissions: UserTabPermissions | null | undefined) =>
    Boolean(permissions?.bidding.canAdd && permissions.estimateGeneralItems.canView);

export const getPackageEstimateTotal = (draft: IBidPackageDraft): number =>
    draft.lineItems.reduce(
        (sum, line) => sum + (line.quantity ?? 0) * (line.estimatedUnitCost ?? 0),
        0,
    );

export interface IEstimateUsage {
    lineCount: number;
    costCodeCount: number;
    total: number;
}

export const getEstimateUsage = (suggestions: IBidPackageSuggestion[]): IEstimateUsage => {
    const lines = suggestions.flatMap((s) => s.draft.lineItems);
    return {
        lineCount: lines.length,
        costCodeCount: new Set(lines.map((line) => line.costCode.id)).size,
        total: suggestions.reduce((sum, s) => sum + getPackageEstimateTotal(s.draft), 0),
    };
};

export const getDistinctCostCodes = (draft: IBidPackageDraft): string[] => [
    ...new Set(draft.lineItems.map((line) => line.costCode.name)),
];

export const formatSubInsight = (insight: ISubBidInsight | null): string | undefined => {
    if (!insight) {
        return undefined;
    }
    if (insight.recentBidsTotal === 0) {
        return "First bid with you";
    }
    return `Won ${insight.recentBidsAwarded} of ${insight.recentBidsTotal} recent bids`;
};

export const formatDeadline = (deadline: BidPackageDraftDeadline | null): string => {
    if (!deadline) {
        return "No deadline";
    }
    const time = deadline.time ? ` at ${deadline.time}` : "";
    if (deadline.kind === "fixed") {
        return `${deadline.date}${time}`;
    }
    const days = Math.abs(deadline.offsetDays);
    const direction = deadline.offsetDays < 0 ? "before" : "after";
    return `${days} ${days === 1 ? "day" : "days"} ${direction} ${deadline.scheduleItem.name}${time}`;
};
